import {
  Box, Typography, CircularProgress, Alert, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip,
  FormControl, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PersonOff as DiscardIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AtsDiscardDialog from './AtsDiscardDialog';

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
  if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleDateString('es-CL');
};

const AtsApplicantsTab = ({
  loading,
  applications,
  allStages,
  updatingStage,
  isSuperAdmin,
  isClosed,
  onStageChange,
  onDiscard,
  onDelete,
  rejectionTemplate = { subject: '', body: '' },
  autoSendDefault = false,
  positionName = '',
  orgName = '',
}) => {
  const { t } = useTranslation();

  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [applicationToDiscard, setApplicationToDiscard] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);

  const activeApplications = applications.filter((a) => a.status !== 'discarded');
  const discardedApplications = applications.filter((a) => a.status === 'discarded');

  const handleDiscardClick = (e, application) => {
    e.stopPropagation();
    setApplicationToDiscard(application);
    setDiscardDialogOpen(true);
  };

  const handleDeleteClick = (e, application) => {
    e.stopPropagation();
    setApplicationToDelete(application);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {activeApplications.length === 0 ? (
        <Alert severity="info">{t('ats.detail.noApplications')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.candidate')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.source')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.stage')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.appliedOn')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.detail.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeApplications.map((application) => {
                const appId = application.uid || application.id;
                const isUpdating = updatingStage === appId;
                return (
                  <TableRow key={appId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {application.candidateName || application.candidateEmail || application.candidateId}
                      </Typography>
                      {application.candidateName && application.candidateEmail && (
                        <Typography variant="caption" color="text.secondary">
                          {application.candidateEmail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Desde una feria, el chip lleva su nombre (PLAN_FERIAS);
                          importada desde un archivo, el portal de origen
                          (PLAN_IMPORTACION_COMPUTRABAJO, fase 1) */}
                      <Chip
                        label={application.fairName
                          ? t('ats.fairs.origin', { name: application.fairName })
                          : application.source === 'import'
                            ? t('ats.import.sourceLabel', { origin: application.importOrigin || '—' })
                            : (application.source || 'portal')}
                        size="small"
                        variant="outlined"
                        color={application.fairName ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {isUpdating ? (
                        <CircularProgress size={20} />
                      ) : application.status === 'pending_validation' ? (
                        // "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0):
                        // fuera de las etapas hasta que se valide en el kanban
                        <Chip
                          label={t('ats.validation.chip')}
                          size="small"
                          sx={{ backgroundColor: '#D97706', color: '#fff', fontWeight: 600 }}
                        />
                      ) : (
                        // Igual que el kanban: cualquier etapa de cualquier fase;
                        // un candidato bloqueado no se mueve (tooltip con el motivo)
                        <Tooltip
                          title={application.status === 'blocked' ? t('ats.stageSelector.lock.blocked') : ''}
                          arrow
                        >
                          <FormControl size="small" fullWidth>
                            <Select
                              value={application.currentStageId || ''}
                              onChange={(e) => onStageChange(application, e.target.value)}
                              displayEmpty
                              disabled={isClosed || application.status === 'blocked'}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              <MenuItem value="" disabled>
                                <Typography variant="body2" color="text.secondary">
                                  {t('ats.detail.selectStage')}
                                </Typography>
                              </MenuItem>
                              {allStages.map((stage) => (
                                <MenuItem key={stage.id} value={stage.id}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {stage.phaseName} /
                                    </Typography>
                                    <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                                      {stage.name}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(application.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('ats.detail.discard')}>
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => handleDiscardClick(e, application)}
                            disabled={isClosed}
                          >
                            <DiscardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {isSuperAdmin && (
                        <Tooltip title={t('ats.detail.deleteApplication')}>
                          <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, application)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Candidatos descartados */}
      {discardedApplications.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }}>
            {t('ats.detail.discarded')} ({discardedApplications.length})
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #FEE2E2', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FEF2F2' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.candidate')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('ats.detail.appliedOn')}</TableCell>
                  {isSuperAdmin && (
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.detail.actions')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {discardedApplications.map((application) => {
                  const appId = application.uid || application.id;
                  return (
                    <TableRow key={appId}>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {application.candidateName || application.candidateEmail || application.candidateId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimestamp(application.createdAt)}
                        </Typography>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell align="center">
                          <Tooltip title={t('ats.detail.deleteApplication')}>
                            <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, application)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Dialog descarte (componente compartido) */}
      <AtsDiscardDialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        application={applicationToDiscard}
        rejectionTemplate={rejectionTemplate}
        autoSendDefault={autoSendDefault}
        positionName={positionName}
        orgName={orgName}
        onConfirm={(opts) => {
          onDiscard(applicationToDiscard, opts);
          setDiscardDialogOpen(false);
          setApplicationToDiscard(null);
        }}
      />

      {/* Dialog eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ats.detail.deleteApplicationTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('ats.detail.deleteApplicationConfirm', {
              name: applicationToDelete?.candidateName || applicationToDelete?.candidateEmail,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">{t('cancel')}</Button>
          <Button
            onClick={() => {
              onDelete(applicationToDelete);
              setDeleteDialogOpen(false);
              setApplicationToDelete(null);
            }}
            color="error"
            variant="contained"
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AtsApplicantsTab;